import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  useDraggable,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, Plus, Printer, Trash2, Package, MapPin, GripVertical, X, Map, Route } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { GoogleMapsLoader, useGoogleMaps } from '../../components/maps/GoogleMapsLoader';
import RouteMapModal from './RouteMapModal';
import apiService from '../../services/apiService';
import './CaratulaPedidosPage.css';

// Punto de origen (almacén/tienda)
const ORIGEN_COORDS = { lat: 22.1456552, lng: -102.27964 };

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Componente draggable para pedidos sin ruta
const DraggablePedidoSinRuta = ({ pedido, isSelected, onSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `sinruta-${pedido.id}`,
    data: {
      type: 'pedido-sin-ruta',
      pedido: pedido,
    },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`pedido-card ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
    >
      <div className="drag-handle-sinruta" {...attributes} {...listeners}>
        <GripVertical size={14} />
      </div>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onSelect(pedido.id)}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="pedido-info" onClick={() => onSelect(pedido.id)}>
        <span className="pedido-folio">#{pedido.folio}</span>
        <span className="pedido-cliente">{pedido.cliente_nombre}</span>
        <span className="pedido-direccion">
          <MapPin size={12} />
          {pedido.direccion_entrega || 'Sin dirección'}
        </span>
      </div>
    </div>
  );
};

// Componente sortable para cada pedido dentro de una ruta
const SortablePedido = ({ pedido, index, rutaId, onQuitar }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `ruta-${rutaId}-pedido-${pedido.venta_id}`,
    data: {
      type: 'pedido-en-ruta',
      pedido: pedido,
      rutaId: rutaId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`ruta-pedido-item ${isDragging ? 'dragging' : ''}`}
    >
      <div className="drag-handle" {...attributes} {...listeners}>
        <GripVertical size={16} />
      </div>
      <span className="pedido-orden">{index + 1}</span>
      <div className="pedido-datos">
        <span className="pedido-folio">#{pedido.folio}</span>
        <span className="pedido-cliente">{pedido.cliente_nombre}</span>
        <span className="pedido-direccion">
          <MapPin size={12} />
          {pedido.direccion_entrega || 'Sin dirección'}
        </span>
      </div>
      <button
        className="quitar-pedido-btn"
        onClick={() => onQuitar(rutaId, pedido.venta_id)}
        title="Quitar de la ruta"
      >
        <X size={14} />
      </button>
    </div>
  );
};

// Componente droppable para cada ruta
const DroppableRuta = ({ ruta, children, isOver }) => {
  const { setNodeRef } = useDroppable({
    id: `droppable-ruta-${ruta.id}`,
    data: {
      type: 'ruta',
      rutaId: ruta.id,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`ruta-pedidos ${isOver ? 'dragging-over' : ''}`}
    >
      {children}
    </div>
  );
};

// Componente para preview durante el drag
const DragPreview = ({ pedido }) => {
  if (!pedido) return null;

  return (
    <div className="drag-preview">
      <span className="pedido-folio">#{pedido.folio}</span>
      <span className="pedido-cliente">{pedido.cliente_nombre}</span>
    </div>
  );
};

const CaratulaPedidosPage = () => {
  const { loading, setLoading, showToast } = useApp();
  const { isLoaded } = useGoogleMaps();

  const [fechaInicio, setFechaInicio] = useState(getTodayDate());
  const [fechaFin, setFechaFin] = useState(getTodayDate());
  const [pedidosSinRuta, setPedidosSinRuta] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [selectedPedidos, setSelectedPedidos] = useState([]);
  const [rutaSeleccionada, setRutaSeleccionada] = useState(null);

  // Estados para drag & drop
  const [activeId, setActiveId] = useState(null);
  const [activePedido, setActivePedido] = useState(null);
  const [overRutaId, setOverRutaId] = useState(null);

  // Modal para crear ruta
  const [showCrearRutaModal, setShowCrearRutaModal] = useState(false);
  const [nuevaRutaNombre, setNuevaRutaNombre] = useState('');
  const [fechaNuevaRuta, setFechaNuevaRuta] = useState(getTodayDate());

  // Modal para ver mapa de ruta
  const [showMapModal, setShowMapModal] = useState(false);
  const [rutaParaMapa, setRutaParaMapa] = useState(null);

  // Estado para optimización de ruta
  const [optimizingRutaId, setOptimizingRutaId] = useState(null);

  // Sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Custom collision detection
  const customCollisionDetection = (args) => {
    // Primero intentar pointerWithin para detectar contenedores
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }

    // Fallback a rectIntersection
    return rectIntersection(args);
  };

  useEffect(() => {
    loadData();
  }, [fechaInicio, fechaFin]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [pedidosResult, rutasResult] = await Promise.all([
        apiService.getPedidosDomicilioSinRuta(fechaInicio, fechaFin),
        apiService.getRutasEntrega(fechaInicio, fechaFin)
      ]);

      if (pedidosResult.success) {
        setPedidosSinRuta(pedidosResult.data || []);
      }

      if (rutasResult.success) {
        // Cargar detalles de cada ruta
        const rutasConPedidos = await Promise.all(
          (rutasResult.data || []).map(async (ruta) => {
            const detalleResult = await apiService.getRutaEntrega(ruta.id);
            return detalleResult.success ? detalleResult.data : { ...ruta, pedidos: [] };
          })
        );
        setRutas(rutasConPedidos);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showToast('Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPedido = (pedidoId) => {
    setSelectedPedidos(prev => {
      if (prev.includes(pedidoId)) {
        return prev.filter(id => id !== pedidoId);
      }
      return [...prev, pedidoId];
    });
  };

  const handleSelectAllPedidos = () => {
    if (selectedPedidos.length === pedidosSinRuta.length) {
      setSelectedPedidos([]);
    } else {
      setSelectedPedidos(pedidosSinRuta.map(p => p.id));
    }
  };

  const handleCrearRuta = async () => {
    if (!nuevaRutaNombre.trim()) {
      showToast('Ingresa un nombre para la ruta', 'warning');
      return;
    }

    try {
      setLoading(true);
      const result = await apiService.crearRutaEntrega(nuevaRutaNombre.trim(), fechaNuevaRuta);

      if (result.success) {
        showToast('Ruta creada exitosamente', 'success');
        setShowCrearRutaModal(false);
        setNuevaRutaNombre('');
        loadData();
      } else {
        showToast(result.message || 'Error al crear ruta', 'error');
      }
    } catch (error) {
      console.error('Error al crear ruta:', error);
      showToast('Error al crear ruta', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarRuta = async (rutaId) => {
    if (!window.confirm('¿Eliminar esta ruta? Los pedidos volverán a estar sin asignar.')) {
      return;
    }

    try {
      setLoading(true);
      const result = await apiService.eliminarRutaEntrega(rutaId);

      if (result.success) {
        showToast('Ruta eliminada', 'success');
        loadData();
      } else {
        showToast(result.message || 'Error al eliminar ruta', 'error');
      }
    } catch (error) {
      console.error('Error al eliminar ruta:', error);
      showToast('Error al eliminar ruta', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAgregarPedidosARuta = async (rutaId, pedidoIds = null) => {
    const idsToAdd = pedidoIds || selectedPedidos;

    if (idsToAdd.length === 0) {
      showToast('Selecciona al menos un pedido', 'warning');
      return;
    }

    try {
      setLoading(true);
      const result = await apiService.agregarPedidosARuta(rutaId, idsToAdd);

      if (result.success) {
        showToast(result.message, 'success');
        setSelectedPedidos([]);
        setRutaSeleccionada(null);
        loadData();
      } else {
        showToast(result.message || 'Error al agregar pedidos', 'error');
      }
    } catch (error) {
      console.error('Error al agregar pedidos:', error);
      showToast('Error al agregar pedidos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuitarPedidoDeRuta = async (rutaId, ventaId) => {
    try {
      setLoading(true);
      const result = await apiService.quitarPedidoDeRuta(rutaId, ventaId);

      if (result.success) {
        showToast('Pedido quitado de la ruta', 'success');
        loadData();
      } else {
        showToast(result.message || 'Error al quitar pedido', 'error');
      }
    } catch (error) {
      console.error('Error al quitar pedido:', error);
      showToast('Error al quitar pedido', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    if (active.data.current?.type === 'pedido-sin-ruta') {
      setActivePedido(active.data.current.pedido);
    } else if (active.data.current?.type === 'pedido-en-ruta') {
      setActivePedido(active.data.current.pedido);
    }
  };

  const handleDragOver = (event) => {
    const { over } = event;

    if (over?.data.current?.type === 'ruta') {
      setOverRutaId(over.data.current.rutaId);
    } else if (over?.id?.toString().startsWith('droppable-ruta-')) {
      const rutaId = parseInt(over.id.toString().replace('droppable-ruta-', ''));
      setOverRutaId(rutaId);
    } else {
      setOverRutaId(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    setActiveId(null);
    setActivePedido(null);
    setOverRutaId(null);

    if (!over) return;

    const activeType = active.data.current?.type;

    // Caso 1: Arrastrar pedido sin ruta a una ruta
    if (activeType === 'pedido-sin-ruta') {
      let targetRutaId = null;

      // Verificar si se soltó sobre un droppable de ruta
      if (over.data.current?.type === 'ruta') {
        targetRutaId = over.data.current.rutaId;
      } else if (over.id?.toString().startsWith('droppable-ruta-')) {
        targetRutaId = parseInt(over.id.toString().replace('droppable-ruta-', ''));
      } else if (over.id?.toString().startsWith('ruta-')) {
        // Si se soltó sobre un pedido dentro de una ruta
        const match = over.id.toString().match(/ruta-(\d+)-pedido-/);
        if (match) {
          targetRutaId = parseInt(match[1]);
        }
      }

      if (targetRutaId) {
        const pedidoId = active.data.current.pedido.id;
        await handleAgregarPedidosARuta(targetRutaId, [pedidoId]);
      }
      return;
    }

    // Caso 2: Reordenar pedidos dentro de una ruta
    if (activeType === 'pedido-en-ruta') {
      const sourceRutaId = active.data.current.rutaId;

      // Verificar si el destino es en la misma ruta
      if (over.id?.toString().startsWith(`ruta-${sourceRutaId}-pedido-`)) {
        const rutaIndex = rutas.findIndex(r => r.id === sourceRutaId);
        if (rutaIndex === -1) return;

        const ruta = rutas[rutaIndex];
        const pedidos = ruta.pedidos || [];

        const oldIndex = pedidos.findIndex(p => `ruta-${sourceRutaId}-pedido-${p.venta_id}` === active.id);
        const newIndex = pedidos.findIndex(p => `ruta-${sourceRutaId}-pedido-${p.venta_id}` === over.id);

        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

        const newPedidos = arrayMove(pedidos, oldIndex, newIndex);

        // Actualizar estado local inmediatamente
        const nuevasRutas = [...rutas];
        nuevasRutas[rutaIndex] = { ...ruta, pedidos: newPedidos };
        setRutas(nuevasRutas);

        // Guardar en el servidor
        try {
          const pedidosOrdenados = newPedidos.map(p => ({ venta_id: p.venta_id }));
          const result = await apiService.actualizarOrdenRuta(sourceRutaId, pedidosOrdenados);

          if (!result.success) {
            showToast('Error al guardar orden', 'error');
            loadData();
          }
        } catch (error) {
          console.error('Error al actualizar orden:', error);
          showToast('Error al guardar orden', 'error');
          loadData();
        }
      }
    }
  };

  const handleImprimirCaratula = async (rutaId) => {
    try {
      setLoading(true);
      const result = await apiService.imprimirCaratula(rutaId);

      if (result.success && result.data.html) {
        const ventanaImpresion = window.open('', 'Imprimir Carátula', 'width=1100,height=700');

        if (ventanaImpresion) {
          ventanaImpresion.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>Carátula de Ruta - ${result.data.ruta.nombre}</title>
            </head>
            <body>
              ${result.data.html}
              <script>
                // Esperar a que JsBarcode cargue y genere los códigos antes de imprimir
                function waitForBarcodes() {
                  if (typeof JsBarcode !== 'undefined') {
                    // Dar tiempo extra para que se generen los códigos
                    setTimeout(function() {
                      window.print();
                    }, 500);
                  } else {
                    // Reintentar si JsBarcode aún no carga
                    setTimeout(waitForBarcodes, 100);
                  }
                }
                window.onload = waitForBarcodes;
              </script>
            </body>
            </html>
          `);
          ventanaImpresion.document.close();
        }
      } else {
        showToast('Error al generar carátula', 'error');
      }
    } catch (error) {
      console.error('Error al imprimir carátula:', error);
      showToast('Error al imprimir carátula', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Geocodificar una dirección
  const geocodeAddress = async (address) => {
    if (!window.google || !address) return null;

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ address: address + ', Aguascalientes, México' });

      if (response.results && response.results[0]) {
        const location = response.results[0].geometry.location;
        return {
          lat: location.lat(),
          lng: location.lng()
        };
      }
    } catch (error) {
      console.error('Error geocoding:', error);
    }
    return null;
  };

  // Optimizar ruta usando Google Directions API
  const handleOptimizarRuta = async (ruta) => {
    if (!isLoaded || !window.google) {
      showToast('Google Maps no está disponible', 'error');
      return;
    }

    if (!ruta.pedidos || ruta.pedidos.length < 2) {
      showToast('Se necesitan al menos 2 paradas para optimizar', 'warning');
      return;
    }

    setOptimizingRutaId(ruta.id);

    try {
      // Obtener coordenadas de todos los pedidos
      const pedidosConCoords = [];

      for (const pedido of ruta.pedidos) {
        let coords = null;

        if (pedido.latitud && pedido.longitud) {
          coords = { lat: pedido.latitud, lng: pedido.longitud };
        } else if (pedido.direccion_entrega) {
          coords = await geocodeAddress(pedido.direccion_entrega);
        }

        if (coords) {
          pedidosConCoords.push({ ...pedido, coords });
        } else {
          showToast(`No se pudo obtener ubicación de: ${pedido.cliente_nombre}`, 'warning');
        }
      }

      if (pedidosConCoords.length < 2) {
        showToast('No hay suficientes paradas con ubicación válida', 'error');
        setOptimizingRutaId(null);
        return;
      }

      // Usar Directions API con optimización de waypoints
      const directionsService = new window.google.maps.DirectionsService();

      // Todas las paradas como waypoints
      const waypoints = pedidosConCoords.map(p => ({
        location: new window.google.maps.LatLng(p.coords.lat, p.coords.lng),
        stopover: true
      }));

      // Ruta circular: Almacén -> paradas optimizadas -> Almacén
      const request = {
        origin: new window.google.maps.LatLng(ORIGEN_COORDS.lat, ORIGEN_COORDS.lng),
        destination: new window.google.maps.LatLng(ORIGEN_COORDS.lat, ORIGEN_COORDS.lng),
        waypoints: waypoints,
        optimizeWaypoints: true,
        travelMode: window.google.maps.TravelMode.DRIVING
      };

      directionsService.route(request, async (result, status) => {
        if (status === 'OK') {
          // Obtener el orden optimizado
          const waypointOrder = result.routes[0].waypoint_order;

          // Reordenar pedidos según el orden optimizado por Google
          const pedidosReordenados = waypointOrder.map(originalIndex => pedidosConCoords[originalIndex]);

          // Actualizar el orden en el servidor
          const pedidosOrdenados = pedidosReordenados.map(p => ({ venta_id: p.venta_id }));
          const updateResult = await apiService.actualizarOrdenRuta(ruta.id, pedidosOrdenados);

          if (updateResult.success) {
            showToast('Ruta optimizada exitosamente', 'success');
            loadData();
          } else {
            showToast('Error al guardar el orden optimizado', 'error');
          }
        } else {
          console.error('Directions API error:', status);
          showToast(`Error al optimizar: ${status}`, 'error');
        }

        setOptimizingRutaId(null);
      });

    } catch (error) {
      console.error('Error al optimizar ruta:', error);
      showToast('Error al optimizar ruta', 'error');
      setOptimizingRutaId(null);
    }
  };

  return (
    <div className="caratula-page">
      {/* Controles */}
      <div className="caratula-controls">
        <div className="caratula-fechas">
          <span className="fecha-label">Pedidos del:</span>
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            className="date-input"
          />
          <span className="fecha-separador">al</span>
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            className="date-input"
          />
        </div>

        <Button onClick={() => {
          setFechaNuevaRuta(fechaInicio);
          setShowCrearRutaModal(true);
        }}>
          <Plus size={18} />
          Nueva Ruta
        </Button>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando...</p>
        </div>
      )}

      {!loading && (
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="caratula-content">
            {/* Panel izquierdo: Pedidos sin ruta */}
            <div className="caratula-panel pedidos-sin-ruta">
              <div className="panel-header">
                <h3>
                  <Package size={18} />
                  Pedidos sin Ruta ({pedidosSinRuta.length})
                </h3>
                {pedidosSinRuta.length > 0 && (
                  <button
                    className="select-all-btn"
                    onClick={handleSelectAllPedidos}
                  >
                    {selectedPedidos.length === pedidosSinRuta.length ? 'Deseleccionar' : 'Seleccionar todos'}
                  </button>
                )}
              </div>

              <div className="panel-content">
                {pedidosSinRuta.length === 0 ? (
                  <div className="empty-state">
                    <Package size={32} />
                    <p>No hay pedidos a domicilio sin ruta</p>
                  </div>
                ) : (
                  <div className="pedidos-list">
                    {pedidosSinRuta.map((pedido) => (
                      <DraggablePedidoSinRuta
                        key={pedido.id}
                        pedido={pedido}
                        isSelected={selectedPedidos.includes(pedido.id)}
                        onSelect={handleSelectPedido}
                      />
                    ))}
                  </div>
                )}
              </div>

              {selectedPedidos.length > 0 && rutas.length > 0 && (
                <div className="panel-actions">
                  <span className="selected-count">{selectedPedidos.length} seleccionado(s)</span>
                  <div className="agregar-a-ruta">
                    <select
                      value={rutaSeleccionada || ''}
                      onChange={(e) => setRutaSeleccionada(e.target.value ? parseInt(e.target.value) : null)}
                    >
                      <option value="">Agregar a ruta...</option>
                      {rutas.map(ruta => (
                        <option key={ruta.id} value={ruta.id}>{ruta.nombre}</option>
                      ))}
                    </select>
                    <Button
                      size="small"
                      onClick={() => rutaSeleccionada && handleAgregarPedidosARuta(rutaSeleccionada)}
                      disabled={!rutaSeleccionada}
                    >
                      Agregar
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Panel derecho: Rutas */}
            <div className="caratula-panel rutas-panel">
              <div className="panel-header">
                <h3>Rutas del {new Date(fechaInicio + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}</h3>
                <div className="rutas-date-filter">
                  <Calendar size={16} />
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => {
                      setFechaInicio(e.target.value);
                      setFechaFin(e.target.value);
                    }}
                    className="date-input-small"
                    title="Filtrar rutas por fecha"
                  />
                </div>
              </div>

              <div className="panel-content rutas-content">
                {rutas.length === 0 ? (
                  <div className="empty-state">
                    <Package size={32} />
                    <p>No hay rutas para el {new Date(fechaInicio + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}</p>
                    <Button size="small" onClick={() => setShowCrearRutaModal(true)}>

                      Crear Ruta
                    </Button>
                  </div>
                ) : (
                  rutas.map((ruta) => (
                    <div key={ruta.id} className="ruta-card">
                      <div className="ruta-header">
                        <div className="ruta-info">
                          <h4>{ruta.nombre}</h4>
                          <span className="ruta-count">{ruta.pedidos?.length || 0} entregas</span>
                          <span className="ruta-fecha">{ruta.fecha}</span>
                        </div>
                        <div className="ruta-actions">
                          <button
                            className={`ruta-action-btn optimize ${optimizingRutaId === ruta.id ? 'optimizing' : ''}`}
                            onClick={() => handleOptimizarRuta(ruta)}
                            title="Optimizar ruta"
                            disabled={!ruta.pedidos?.length || ruta.pedidos?.length < 2 || optimizingRutaId !== null}
                          >
                            {optimizingRutaId === ruta.id ? (
                              <div className="btn-spinner"></div>
                            ) : (
                              <Route size={16} />
                            )}
                          </button>
                          <button
                            className="ruta-action-btn map"
                            onClick={() => {
                              setRutaParaMapa(ruta);
                              setShowMapModal(true);
                            }}
                            title="Ver en mapa"
                            disabled={!ruta.pedidos?.length}
                          >
                            <Map size={16} />
                          </button>
                          <button
                            className="ruta-action-btn print"
                            onClick={() => handleImprimirCaratula(ruta.id)}
                            title="Imprimir carátula"
                            disabled={!ruta.pedidos?.length}
                          >
                            <Printer size={16} />
                          </button>
                          <button
                            className="ruta-action-btn delete"
                            onClick={() => handleEliminarRuta(ruta.id)}
                            title="Eliminar ruta"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <DroppableRuta ruta={ruta} isOver={overRutaId === ruta.id}>
                        <SortableContext
                          items={(ruta.pedidos || []).map(p => `ruta-${ruta.id}-pedido-${p.venta_id}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          {(!ruta.pedidos || ruta.pedidos.length === 0) ? (
                            <div className="ruta-empty">
                              <Package size={20} />
                              <p>Arrastra pedidos aquí</p>
                            </div>
                          ) : (
                            ruta.pedidos.map((pedido, index) => (
                              <SortablePedido
                                key={pedido.venta_id}
                                pedido={pedido}
                                index={index}
                                rutaId={ruta.id}
                                onQuitar={handleQuitarPedidoDeRuta}
                              />
                            ))
                          )}
                        </SortableContext>
                      </DroppableRuta>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Overlay durante el drag */}
          <DragOverlay>
            {activePedido ? <DragPreview pedido={activePedido} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Modal para crear ruta */}
      {showCrearRutaModal && (
        <Modal
          title="Nueva Ruta"
          onClose={() => {
            setShowCrearRutaModal(false);
            setNuevaRutaNombre('');
          }}
          size="small"
        >
          <div className="crear-ruta-form">
            <div className="form-group">
              <label>Nombre de la ruta</label>
              <input
                type="text"
                value={nuevaRutaNombre}
                onChange={(e) => setNuevaRutaNombre(e.target.value)}
                placeholder="Ej: Ruta Mañana Centro"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Fecha de la ruta</label>
              <input
                type="date"
                value={fechaNuevaRuta}
                onChange={(e) => setFechaNuevaRuta(e.target.value)}
              />
            </div>
            <div className="form-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCrearRutaModal(false);
                  setNuevaRutaNombre('');
                }}
              >
                Cancelar
              </Button>
              <Button onClick={handleCrearRuta}>
                Crear Ruta
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal para ver mapa de ruta */}
      {showMapModal && rutaParaMapa && (
        <RouteMapModal
          ruta={rutaParaMapa}
          onClose={() => {
            setShowMapModal(false);
            setRutaParaMapa(null);
          }}
        />
      )}
    </div>
  );
};

// Wrapper con GoogleMapsLoader
const CaratulaPedidosPageWrapper = () => {
  return (
    <GoogleMapsLoader>
      <CaratulaPedidosPage />
    </GoogleMapsLoader>
  );
};

export default CaratulaPedidosPageWrapper;
